import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, MessageSquare, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SEOHead } from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nom: "", email: "", telephone: "", message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé !", {
        description: "Nous vous répondrons dans les plus brefs délais."
      });
      setForm({ nom: "", email: "", telephone: "", message: "" });
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Téléphone",
      value: "06 25 16 80 27",
      href: "tel:0625168027",
      desc: "Du lundi au samedi, 8h-19h"
    },
    {
      icon: Mail,
      title: "Email",
      value: "contact@iks-services.fr",
      href: "mailto:contact@iks-services.fr",
      desc: "Réponse sous 24h"
    },
    {
      icon: MapPin,
      title: "Zone d'intervention",
      value: "Tout l'Hérault (34)",
      desc: "Montpellier, Béziers, Sète..."
    },
    {
      icon: Clock,
      title: "Horaires",
      value: "7j/7",
      desc: "Interventions sur rendez-vous"
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50" data-testid="contact-page">
      <SEOHead 
        title="Contact — Devis Gratuit Nettoyage Hérault"
        description="Contactez IKS Propreté & Services pour un devis gratuit. Société de nettoyage dans l'Hérault : ménage à domicile, nettoyage de vitres, entretien de bureaux. Téléphone : 06 25 16 80 27. Montpellier et tout le 34."
        path="/contact"
        keywords="contact société nettoyage Hérault, devis nettoyage Montpellier, devis ménage gratuit 34, contacter entreprise nettoyage Hérault"
      />
      
      {/* Hero */}
      <section className="bg-[#003264] py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white/80 text-sm font-medium mb-6">
            <MessageSquare className="w-4 h-4" />
            Contact
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Parlons de vos besoins
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Une question, un devis ? Notre équipe est à votre écoute pour vous accompagner.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 text-center card-hover">
                <div className="w-14 h-14 rounded-2xl bg-[#003264]/10 flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-7 h-7 text-[#003264]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{info.title}</h3>
                {info.href ? (
                  <a href={info.href} className="text-[#003264] font-semibold hover:underline">
                    {info.value}
                  </a>
                ) : (
                  <p className="text-[#003264] font-semibold">{info.value}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">{info.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-[#003264] mb-3">Envoyez-nous un message</h2>
            <p className="text-gray-600">Nous vous répondrons dans les plus brefs délais</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl shadow-gray-100/50 p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-gray-700">Nom complet *</Label>
                  <Input 
                    name="nom" 
                    value={form.nom} 
                    onChange={handleChange} 
                    required 
                    className="mt-1.5 h-12"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Téléphone</Label>
                  <Input 
                    name="telephone" 
                    value={form.telephone} 
                    onChange={handleChange} 
                    className="mt-1.5 h-12"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-700">Email *</Label>
                <Input 
                  name="email" 
                  type="email"
                  value={form.email} 
                  onChange={handleChange} 
                  required 
                  className="mt-1.5 h-12"
                  placeholder="jean.dupont@email.com"
                />
              </div>

              <div>
                <Label className="text-gray-700">Votre message *</Label>
                <Textarea 
                  name="message" 
                  value={form.message} 
                  onChange={handleChange} 
                  required
                  rows={5}
                  className="mt-1.5 resize-none"
                  placeholder="Décrivez votre demande..."
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full bg-[#003264] hover:bg-[#002244] text-white h-12 text-base btn-shine"
              >
                {loading ? "Envoi en cours..." : (
                  <>
                    Envoyer le message
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-500">
                <CheckCircle className="w-4 h-4 inline mr-1 text-emerald-500" />
                Vos données sont protégées et ne seront jamais partagées
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-xl font-bold text-[#003264] mb-6">Besoin d'une réponse immédiate ?</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:0625168027">
              <Button size="lg" className="bg-[#003264] hover:bg-[#002244] text-white px-8">
                <Phone className="w-5 h-5 mr-2" />
                Appelez-nous
              </Button>
            </a>
            <a href="https://wa.me/33625168027" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-[#003264] text-[#003264] hover:bg-[#003264] hover:text-white px-8">
                <MessageSquare className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
